import type { CourseAccreditationByProfession } from "@brookbush/types";

import { Disclosure } from "@headlessui/react";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";
import Image from "next/image";
import { memo } from "react";

import TextBlock from "~/components/TextBlock";

interface AboutAccreditationTabPanelProps {
	profession: CourseAccreditationByProfession;
}
const AboutAccreditationTabPanel = ({
	profession,
}: AboutAccreditationTabPanelProps) => {
	return (
		<div className="mx-auto flex w-full max-w-4xl flex-col gap-4">
			<LayoutGroup>
				{profession?.accreditations.map((accreditation) => (
					<Disclosure key={accreditation._id} as={motion.div} layout="position">
						{({ open }) => (
							<>
								<Disclosure.Button className="flex w-full items-center justify-between gap-4 rounded-xl border px-4 py-2.5 text-left text-lg hover:bg-slate-50/50 dark:hover:bg-slate-700">
									{accreditation.abbreviation} - {accreditation.title}{" "}
									{open ? (
										<ChevronUp size={22} className="shrink-0" />
									) : (
										<ChevronDown size={22} className="shrink-0" />
									)}
								</Disclosure.Button>
								<AnimatePresence>
									{open && (
										<Disclosure.Panel
											initial={{
												opacity: 0,
												y: -10,
											}}
											animate={{
												opacity: 1,
												y: 0,
												transition: {
													duration: 0.2,
												},
											}}
											exit={{
												opacity: 0,
												y: -10,
												transition: {
													duration: 0.1,
												},
											}}
											static
											as={motion.div}
											className="-mb-4 flex flex-col items-start gap-4 px-4 py-6 text-left sm:flex-row sm:gap-6"
										>
											<div className="relative aspect-[2/1] w-full shrink-0 overflow-hidden rounded-2xl border sm:aspect-square sm:w-32 dark:bg-white">
												<Image
													src={accreditation.thumbnail.url}
													fill
													className="object-contain p-4"
													alt={
														accreditation.thumbnail.alt || accreditation.title
													}
												/>
											</div>
											<div className="flex flex-col gap-2">
												<h3 className="font-medium text-xl sm:text-2xl">
													{accreditation.title}
												</h3>
												<TextBlock value={accreditation.body} />
											</div>
										</Disclosure.Panel>
									)}
								</AnimatePresence>
							</>
						)}
					</Disclosure>
				))}
			</LayoutGroup>
		</div>
	);
};

export default memo(AboutAccreditationTabPanel);
